export interface NearbyTempleDefaultItem {
  id: string;
  name: string;
  distance: string;
  deity: string;
  location: string;
  description: string;
  color: string;
  imageUrl?: string;
  mapUrl?: string;
  lat?: number;
  lng?: number;
}

export const KOTTAYIL_DISTANCE_REFERENCE = 'Distance from Kottayil Shiva Temple';

const buildTempleMapSearchUrl = (name: string, location: string) =>
  `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(`${name}, ${location}`)}`;

export const KOTTAYIL_NEARBY_TEMPLE_DEFAULTS: NearbyTempleDefaultItem[] = [
  {
    id: 't1',
    name: 'Sri Shakthan Kulangara Temple',
    distance: '3.7 km',
    deity: 'Bhagavathy',
    location: 'Near Kollam - Meppayyur Road',
    description: 'Popular local temple known for regular poojas and community festivals.',
    color: '#ef4444',
    imageUrl: '',
    mapUrl: buildTempleMapSearchUrl('Sri Shakthan Kulangara Temple', 'Near Kollam - Meppayyur Road, Koyilandy, Kerala'),
  },
  {
    id: 't2',
    name: 'Sri Kollam Pisharikavu Temple',
    distance: '4.6 km',
    deity: 'Bhagavathy',
    location: 'Koyilandy - Kollam area',
    description: 'Historic temple with strong regional devotional importance.',
    color: '#f59e0b',
    imageUrl: '',
    mapUrl: buildTempleMapSearchUrl('Sri Kollam Pisharikavu Temple', 'Koyilandy - Kollam area, Kerala'),
  },
  {
    id: 't3',
    name: 'Kollam Thali Sree Mahadeva Temple',
    distance: '4.4 km',
    deity: 'Lord Shiva',
    location: 'Kollam, near Koyilandy',
    description: 'Traditional Shiva temple visited by devotees on Mondays and festival days.',
    color: '#8b5cf6',
    imageUrl: '',
    lat: 11.4592656,
    lng: 75.6738217,
  },
  {
    id: 't4',
    name: 'Kizhur Siva Temple',
    distance: '4.8 km',
    deity: 'Lord Shiva',
    location: 'Kizhur',
    description: 'Neighbourhood temple with a calm setting and daily rituals.',
    color: '#22c55e',
    imageUrl: '',
    mapUrl: buildTempleMapSearchUrl('Kizhur Siva Temple', 'Kizhur, Koyilandy, Kerala'),
  },
  {
    id: 't5',
    name: 'Ngareswaram Sri Siva Temple',
    distance: '4.8 km',
    deity: 'Lord Shiva',
    location: 'Near Payyoli - Koyilandy belt',
    description: 'Sacred Shiva shrine on a convenient route for a temple circuit.',
    color: '#3b82f6',
    imageUrl: '',
    mapUrl: buildTempleMapSearchUrl('Ngareswaram Sri Siva Temple', 'Payyoli - Koyilandy, Kerala'),
  },
  {
    id: 't6',
    name: 'Sree Mahavishnu Temple, Payyoli',
    distance: '5.2 km',
    deity: 'Lord Vishnu',
    location: 'Payyoli',
    description: 'Well-known Vishnu temple frequently included in one-day pilgrim trips.',
    color: '#ec4899',
    imageUrl: '',
    lat: 11.5091575,
    lng: 75.6176408,
  },
  {
    id: 't13',
    name: 'Lokanarkavu Temple',
    distance: '12.2 km',
    deity: 'Durga / Bhagavathy',
    location: 'Memunda, Vadakara side',
    description: 'Major historic temple complex and a known cultural landmark in the region.',
    color: '#a855f7',
    imageUrl: '',
    mapUrl: buildTempleMapSearchUrl('Lokanarkavu Temple', 'Memunda, Vadakara, Kerala'),
  },
  {
    id: 't14',
    name: 'Ananthapuram Mahavishnu Temple',
    distance: '4.3 km',
    deity: 'Lord Mahavishnu',
    location: 'Ananthapuram, Kollam, Koyilandy',
    description: 'Traditional Mahavishnu temple in the Kollam - Koyilandy area with regular daily rituals.',
    color: '#0ea5e9',
    imageUrl: '',
    mapUrl: buildTempleMapSearchUrl('Ananthapuram Mahavishnu Temple', 'Ananthapuram, Kollam, Koyilandy, Kerala'),
  },
  {
    id: 't15',
    name: 'Kothamangalam Ayyappa Temple',
    distance: '7.8 km',
    deity: 'Lord Ayyappa',
    location: 'Kothamangalam, Koyilandy',
    description: 'Ayyappa temple often included in local pilgrimage routes with nearby Shiva and Vishnu shrines.',
    color: '#16a34a',
    imageUrl: '',
    mapUrl: buildTempleMapSearchUrl('Kothamangalam Ayyappa Temple', 'Kothamangalam, Koyilandy, Kerala'),
  },
  {
    id: 't16',
    name: 'Urupunnyakavil Temple',
    distance: '3.5 km',
    deity: 'Bhagavathy',
    location: 'Urupunnyakavu, Muchukunnu',
    description: 'Ancient Bhagavathy temple near Muchukunnu known for its spiritual significance and traditional rituals.',
    color: '#d946ef',
    imageUrl: '',
    mapUrl: buildTempleMapSearchUrl('Urupunnyakavil Temple', 'Urupunnyakavu, Muchukunnu, Kerala'),
  },
];

interface NearbyTempleLike {
  id?: string;
  name?: string;
  distance?: string;
  deity?: string;
  location?: string;
  description?: string;
  color?: string;
  imageUrl?: string;
  mapUrl?: string;
  lat?: number | string;
  lng?: number | string;
}

const normalizeTempleName = (name: string) => name.toLowerCase().replace(/\s+/g, ' ').trim();

const toOptionalNumber = (value: unknown) => {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string' && value.trim().length > 0) {
    const parsed = Number(value);
    if (Number.isFinite(parsed)) return parsed;
  }
  return undefined;
};

export const mergeWithKottayilTempleDefaults = (
  temples: NearbyTempleLike[] = []
): NearbyTempleDefaultItem[] => {
  const existing = Array.isArray(temples)
    ? temples.filter((item): item is NearbyTempleLike => !!item && typeof item.name === 'string' && item.name.trim().length > 0)
    : [];

  const defaultsByName = new Map(
    KOTTAYIL_NEARBY_TEMPLE_DEFAULTS.map((item) => [normalizeTempleName(item.name), item] as const)
  );

  const normalizedExisting: NearbyTempleDefaultItem[] = existing.map((item, index) => {
    const normalizedName = normalizeTempleName(item.name || '');
    const fallback = defaultsByName.get(normalizedName);
    return {
      id: item.id || `existing-${index + 1}`,
      name: (item.name || fallback?.name || '').trim(),
      distance: item.distance || fallback?.distance || '',
      deity: item.deity || fallback?.deity || '',
      location: item.location || fallback?.location || '',
      description: item.description || fallback?.description || '',
      color: item.color || fallback?.color || '#9c27b0',
      imageUrl: item.imageUrl || fallback?.imageUrl || '',
      mapUrl: item.mapUrl || fallback?.mapUrl || '',
      lat: toOptionalNumber(item.lat) ?? fallback?.lat,
      lng: toOptionalNumber(item.lng) ?? fallback?.lng,
    };
  });

  const seen = new Set(normalizedExisting.map((item) => normalizeTempleName(item.name)));
  const merged: NearbyTempleDefaultItem[] = [...normalizedExisting];

  KOTTAYIL_NEARBY_TEMPLE_DEFAULTS.forEach((temple) => {
    const key = normalizeTempleName(temple.name);
    if (!seen.has(key)) {
      merged.push({ ...temple, imageUrl: temple.imageUrl || '' });
      seen.add(key);
    }
  });

  return merged;
};
